import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { useEffect, useMemo, useState } from 'preact/hooks';
import { leadsApi } from '../../api/leads';
import { IncludeNonAudienceToggle } from '@/components/common/IncludeNonAudienceToggle';
import { SignalBars } from '@/components/common/SignalBars';
import { useAIChatContextUpdater } from '@/hooks';
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat';
import { LeadSidePanel } from '@/components/common/LeadSidePanel';
import { PersonAvatar, CompanyAvatar } from '@/components/common/Avatar';
import { PhoneEnrichmentCell } from '@/components/common/PhoneEnrichmentCell';
import { EnrichmentStatusCell } from '@/components/common/EnrichmentStatusCell';
import { useUserProfile } from '@/hooks/useUserProfile';
import { getWebTypeMeta } from '../../constants/webTypes';
import { timeFromNowVerbose } from '@/utils/time';
import { RefreshCcw } from 'lucide-preact';
import { getLeadComparator, getCompanyComparator } from '@/utils/leadSorting';
import { getLeadDisplayName, shouldShowEmailLine } from '@/utils/leadFormatting';
import { LoadingRow, ErrorState, EmptyState } from '@/components/common/TableFragments';
import { DataGrid } from '@/components/common/grids';
import { useCompany } from '@/hooks/useCompany';
import { LEAD_EXPORT_COLUMNS, COMPANY_EXPORT_COLUMNS, generateExportFilename } from '@/utils/leadExport';
function useLeadsMode() {
    const initial = (() => {
        try {
            const p = new URLSearchParams(window.location.search).get('mode');
            return p === 'companies' ? 'companies' : 'contacts';
        }
        catch {
            return 'contacts';
        }
    })();
    const [mode, setMode] = useState(initial);
    const update = (next) => {
        setMode(next);
        try {
            const url = new URL(window.location.href);
            if (next === 'contacts')
                url.searchParams.delete('mode');
            else
                url.searchParams.set('mode', next);
            window.history.replaceState({}, '', url.toString());
        }
        catch { /* ignore */ }
    };
    return [mode, update];
}
export const CampaignLeadsTab = ({ clientCompanyId, workspaceId, campaignId }) => {
    const { company, currentWorkspace } = useCompany();
    const { getWorkspaceCapabilities } = useUserProfile();
    const { canEdit } = getWorkspaceCapabilities(clientCompanyId, workspaceId);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mode, setMode] = useLeadsMode();
    const [includeNonAudience, setIncludeNonAudience] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const EMPTY_ANALYTICS = { CampaignId: campaignId, Leads: [], Companies: [], LeadCount: 0, CompanyCount: 0, GeneratedAtUtc: new Date().toISOString() };
    const { updateContext } = useAIChatContextUpdater();
    const load = (signal) => {
        setLoading(true);
        setError(null);
        leadsApi.getCampaignLeads(campaignId, workspaceId, signal)
            .then(r => {
            const payload = r?.data;
            if (payload && Array.isArray(payload.Leads)) {
                setData(payload);
            }
            else {
                setData(EMPTY_ANALYTICS);
            }
        })
            .catch(e => {
            if (e instanceof Error && e.name === 'AbortError')
                return;
            setError(e?.message || 'Unknown error');
            setData(EMPTY_ANALYTICS);
        })
            .finally(() => setLoading(false));
    };
    useEffect(() => {
        const abortController = new AbortController();
        load(abortController.signal);
        return () => abortController.abort();
    }, [campaignId]);
    const generatedRelative = data ? timeFromNowVerbose(data.GeneratedAtUtc) : '—';
    const leads = data?.Leads || [];
    const companies = data?.Companies || [];
    const hasOnlineEvent = leads.some(l => l.WebTypes?.includes('Online Event'));
    const filteredLeads = (mode === 'contacts')
        ? (includeNonAudience ? leads : leads.filter(l => l.IsAudienceTarget !== false))
        : leads;
    // Update AI context when data loads
    useEffect(() => {
        if (!data)
            return;
        updateContext({
            workspaceId,
            campaignId,
            location: ChatLocations.CampaignLeads,
            entityType: ChatEntityTypes.Campaign,
            viewMode: ChatViewModes.View,
            availableFields: [
                { name: 'totalLeads', displayName: 'Total Leads', fieldType: 'number', currentValue: data.LeadCount.toString(), isEditable: false, isFocused: false },
                { name: 'totalCompanies', displayName: 'Total Companies', fieldType: 'number', currentValue: data.CompanyCount.toString(), isEditable: false, isFocused: false },
                { name: 'viewMode', displayName: 'View Mode', fieldType: 'text', currentValue: mode, isEditable: false, isFocused: false },
            ],
            availableActions: [
                'View Lead Details',
                'Export Leads',
                'Toggle Contacts/Companies View',
                'Filter Leads',
            ],
        });
    }, [data, mode, workspaceId, campaignId, updateContext]);
    // Deselect lead if it's filtered out
    useEffect(() => {
        if (selectedLead && !filteredLeads.some(l => l.LeadId === selectedLead.LeadId)) {
            setSelectedLead(null);
        }
    }, [filteredLeads, selectedLead]);
    // Close on ESC
    useEffect(() => {
        if (!selectedLead)
            return;
        const handler = (e) => { if (e.key === 'Escape')
            setSelectedLead(null); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [selectedLead]);
    // Apply partial lead/contact updates from side panel without full reload
    const handleLeadUpdated = (updated) => {
        setData(prev => {
            if (!prev)
                return prev;
            const nextLeads = prev.Leads.map(l => {
                if (l.LeadId !== updated.LeadId)
                    return l;
                const merged = { ...l, ...updated };
                if (updated.FirstName !== undefined || updated.LastName !== undefined) {
                    const fn = [merged.FirstName, merged.LastName].filter(Boolean).join(' ');
                    merged.FullName = fn.length > 0 ? fn : merged.FullName;
                }
                return merged;
            });
            return { ...prev, Leads: nextLeads };
        });
        setSelectedLead(curr => {
            if (!curr || curr.LeadId !== updated.LeadId)
                return curr;
            const merged = { ...curr, ...updated };
            if (updated.FirstName !== undefined || updated.LastName !== undefined) {
                const fn = [merged.FirstName, merged.LastName].filter(Boolean).join(' ');
                merged.FullName = fn.length > 0 ? fn : merged.FullName;
            }
            return merged;
        });
    };
    // ---------- Contact columns for DataGrid ----------
    const contactColumns = useMemo(() => {
        const cols = [
            {
                key: 'lead',
                header: 'Lead',
                sortable: true,
                required: true,
                className: 'whitespace-nowrap',
                render: (l) => {
                    const name = getLeadDisplayName(l);
                    const showEmail = shouldShowEmailLine(l, name);
                    return (_jsxs("div", { class: "flex items-center gap-3", children: [_jsx(PersonAvatar, { firstName: l.FirstName, lastName: l.LastName, fullName: l.FullName, profilePicUrl: l.ProfilePicUrl, alt: name }), _jsxs("div", { class: "flex flex-col leading-tight min-w-0", children: [_jsx("span", { class: "font-medium text-gray-900 dark:text-gray-100 truncate max-w-[180px]", title: name, children: name }), showEmail && l.Email && _jsx("span", { class: "text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]", title: l.Email, children: l.Email })] })] }));
                },
            },
            {
                key: 'phone',
                header: 'Phone',
                sortable: true,
                render: (l, index) => (_jsx(PhoneEnrichmentCell, { phone: l.Phone, globalContactPhone: l.GlobalContactPhone, phoneEnrichmentAvailableDate: l.PhoneEnrichmentAvailableDate, id: l.LeadId, clientCompanyId: clientCompanyId, workspaceId: workspaceId, campaignId: campaignId, canEdit: canEdit, isLastRow: index === filteredLeads.length - 1 && filteredLeads.length > 1, onPhoneUpdated: (updated) => {
                        const finalUpdate = {
                            ...updated,
                            LeadId: l.LeadId,
                            Phone: updated.GlobalContactPhone || updated.Phone
                        };
                        console.log('[CampaignLeadsTab] onPhoneUpdated received:', { updated, finalUpdate, leadId: l.LeadId });
                        handleLeadUpdated(finalUpdate);
                    } })),
            },
            {
                key: 'enrichment',
                header: 'Enrichment',
                sortable: true,
                render: (l) => (_jsx(EnrichmentStatusCell, { enrichmentStatus: l.EnrichmentStatus, enrichedAt: l.EnrichedAt, enrichmentAvailableDate: l.EnrichmentAvailableDate, enrichmentAttemptCount: l.EnrichmentAttemptCount, clientCompanyId: clientCompanyId, workspaceId: workspaceId, campaignId: campaignId, id: l.LeadId, onEnrichmentUpdated: (updated) => handleLeadUpdated({ ...updated, LeadId: l.LeadId }) })),
            },
            {
                key: 'company',
                header: 'Company',
                sortable: true,
                className: 'truncate max-w-[160px]',
                render: (l) => _jsx("span", { title: l.Company || '—', children: l.Company || '—' }),
            },
            {
                key: 'title',
                header: 'Title',
                sortable: true,
                className: 'truncate max-w-[180px]',
                render: (l) => _jsx("span", { title: l.JobTitle || '—', children: l.JobTitle || '—' }),
            },
            {
                key: 'types',
                header: 'Types',
                render: (l) => (_jsx("div", { class: "flex flex-wrap gap-1", children: l.WebTypes?.map(wt => {
                        const meta = getWebTypeMeta(wt);
                        return _jsx("span", { class: `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${meta?.chipClass || 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`, children: wt }, wt);
                    }) })),
            },
        ];
        if (hasOnlineEvent) {
            cols.push({
                key: 'webinarAttended',
                header: 'Webinar Attended',
                sortable: true,
                defaultVisible: false,
                className: 'whitespace-nowrap',
                render: (l) => _jsx(_Fragment, { children: l.WebinarAttended === true ? 'Yes' : l.WebinarAttended === false ? 'No' : '—' }),
            });
        }
        cols.push({
            key: 'lastConversion',
            header: 'Last Conversion',
            sortable: true,
            className: 'whitespace-nowrap',
            render: (l) => _jsx(_Fragment, { children: l.LastInteractionAt ? timeFromNowVerbose(l.LastInteractionAt) : '—' }),
        }, {
            key: 'signal',
            header: 'Campaign Signal',
            sortable: true,
            required: true,
            className: 'whitespace-nowrap',
            render: (l) => _jsx(SignalBars, { score: l.SignalScore }),
        });
        return cols;
    }, [clientCompanyId, workspaceId, campaignId, hasOnlineEvent, filteredLeads.length]);
    // ---------- Company columns for DataGrid ----------
    const companyColumns = useMemo(() => [
        {
            key: 'company',
            header: 'Company',
            sortable: true,
            required: true,
            className: 'whitespace-nowrap',
            render: (c) => (_jsxs("div", { class: "flex items-center gap-3", children: [_jsx(CompanyAvatar, { companyName: c.CompanyName, logoUrl: c.CompanyLogoUrl }), _jsx("div", { children: c.CompanyLinkedInUrl
                            ? _jsx("a", { href: c.CompanyLinkedInUrl, target: "_blank", rel: "noopener noreferrer", class: "font-medium text-gray-900 dark:text-gray-100 hover:underline", title: c.CompanyLinkedInUrl, children: c.CompanyName })
                            : c.CompanyWebsite
                                ? _jsx("a", { href: c.CompanyWebsite, target: "_blank", rel: "noopener noreferrer", class: "font-medium text-gray-900 dark:text-gray-100 hover:underline", title: c.CompanyWebsite, children: c.CompanyName })
                                : _jsx("span", { class: "font-medium text-gray-900 dark:text-gray-100", children: c.CompanyName }) })] })),
        },
        {
            key: 'url',
            header: 'URL',
            className: 'truncate max-w-[180px]',
            render: (c) => {
                if (!c.CompanyWebsite)
                    return _jsx(_Fragment, { children: '—' });
                try {
                    const host = new URL(c.CompanyWebsite).hostname.replace(/^www\./, '');
                    return _jsx("a", { href: c.CompanyWebsite, target: "_blank", rel: "noopener noreferrer", class: "hover:underline", title: c.CompanyWebsite, children: host });
                }
                catch {
                    return _jsx("a", { href: c.CompanyWebsite, target: "_blank", rel: "noopener noreferrer", class: "hover:underline", title: c.CompanyWebsite, children: c.CompanyWebsite });
                }
            },
        },
        {
            key: 'industry',
            header: 'Industry',
            sortable: true,
            className: 'truncate max-w-[160px]',
            render: (c) => _jsx("span", { title: c.Industry || '—', children: c.Industry || '—' }),
        },
        {
            key: 'size',
            header: 'Size',
            sortable: true,
            render: (c) => _jsx(_Fragment, { children: c.StaffCountRange || '—' }),
        },
        {
            key: 'lastConversion',
            header: 'Last Conversion',
            sortable: true,
            className: 'whitespace-nowrap',
            render: (c) => _jsx(_Fragment, { children: c.LastConversionAt ? timeFromNowVerbose(c.LastConversionAt) : '—' }),
        },
        {
            key: 'leads',
            header: 'Leads',
            sortable: true,
            render: (c) => _jsx(_Fragment, { children: c.LeadCount }),
        },
        {
            key: 'signal',
            header: 'Campaign Signal',
            sortable: true,
            required: true,
            className: 'whitespace-nowrap',
            render: (c) => _jsx(SignalBars, { score: c.SignalScore }),
        },
    ], []);
    // ---------- Export configs for DataGrid ----------
    const contactExportConfig = useMemo(() => ({
        getFilename: () => generateExportFilename(company?.Name, currentWorkspace?.Name, 'Campaign', 'leads'),
        columns: LEAD_EXPORT_COLUMNS.map(col => ({ header: col.label, getValue: col.getValue })),
    }), [company?.Name, currentWorkspace?.Name]);
    const companyExportConfig = useMemo(() => ({
        getFilename: () => generateExportFilename(company?.Name, currentWorkspace?.Name, 'Campaign', 'companies'),
        columns: COMPANY_EXPORT_COLUMNS.map(col => ({ header: col.label, getValue: col.getValue })),
    }), [company?.Name, currentWorkspace?.Name]);
    const MetricsBar = () => (_jsxs("div", { class: "flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between mb-3", children: [_jsxs("div", { class: "flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-600 dark:text-gray-300", children: [_jsxs("span", { children: [_jsx("span", { class: "font-semibold text-gray-900 dark:text-gray-100", children: "Conversions:" }), " ", data?.LeadCount ?? '—'] }), _jsxs("span", { children: [_jsx("span", { class: "font-semibold text-gray-900 dark:text-gray-100", children: "Companies:" }), " ", data?.CompanyCount ?? '—'] }), _jsxs("span", { class: "hidden xs:inline", children: [_jsx("span", { class: "font-semibold text-gray-900 dark:text-gray-100", children: "Generated:" }), " ", generatedRelative] }), _jsx(IncludeNonAudienceToggle, { value: includeNonAudience, onToggle: () => setIncludeNonAudience(v => !v) })] }), _jsxs("div", { class: "flex items-center gap-2", children: [_jsx(LeadsModeSwitch, { mode: mode, onChange: (m) => { setMode(m); } }), _jsxs("button", { onClick: () => load(), class: "inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700", title: "Refresh", children: [_jsx(RefreshCcw, { class: "w-4 h-4" }), "Refresh"] })] })] }));
    if (loading)
        return (_jsxs("div", { class: "w-full", children: [_jsx(MetricsBar, {}), _jsx("table", { class: "min-w-full text-sm", children: _jsx("tbody", { children: Array.from({ length: 3 }).map((_, i) => _jsx(LoadingRow, {}, i)) }) })] }));
    if (error)
        return (_jsxs("div", { children: [_jsx(MetricsBar, {}), _jsx(ErrorState, { error: error, onRetry: () => load() })] }));
    const showLeads = mode === 'contacts';
    const showCompanies = mode === 'companies';
    if (showLeads && filteredLeads.length === 0)
        return (_jsxs("div", { children: [_jsx(MetricsBar, {}), _jsx(EmptyState, { message: includeNonAudience ? 'No leads yet.' : 'No audience leads yet.' })] }));
    if (showCompanies && companies.length === 0)
        return (_jsxs("div", { children: [_jsx(MetricsBar, {}), _jsx(EmptyState, { message: "No company signal yet." })] }));
    return (_jsxs(_Fragment, { children: [_jsxs("div", { class: "w-full", children: [_jsx(MetricsBar, {}), showLeads && (_jsx(DataGrid, { data: filteredLeads, columns: contactColumns, getRowKey: (l) => l.LeadId, onRowClick: setSelectedLead, getSortComparator: getLeadComparator, scrollable: true, columnVisibilityKey: "orbitalx-lead-columns-campaign", exportConfig: contactExportConfig })), showCompanies && (_jsx(DataGrid, { data: companies, columns: companyColumns, getRowKey: (c) => c.CompanyId || c.CompanyName, getSortComparator: getCompanyComparator, scrollable: true, columnVisibilityKey: "orbitalx-company-columns-campaign", exportConfig: companyExportConfig }))] }), selectedLead && (_jsx(LeadSidePanel, { lead: selectedLead, onClose: () => setSelectedLead(null), touchpoints: selectedLead.CampaignTouchpoints, clientCompanyId: clientCompanyId, workspaceId: workspaceId, campaignId: campaignId, onLeadUpdated: handleLeadUpdated, onRefreshNeeded: () => load(), onLeadDeleted: (leadId) => {
                    setData(prev => {
                        if (!prev)
                            return prev;
                        return {
                            ...prev,
                            Leads: prev.Leads.filter(l => l.LeadId !== leadId),
                            LeadCount: prev.LeadCount - 1
                        };
                    });
                    setSelectedLead(null);
                } }))] }));
};
const LeadsModeSwitch = ({ mode, onChange }) => {
    const options = [
        { key: 'contacts', label: 'Contacts' },
        { key: 'companies', label: 'Companies' }
    ];
    return (_jsx("div", { role: "radiogroup", "aria-label": "Leads view mode", class: "inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden", children: options.map(o => {
            const active = o.key === mode;
            return (_jsx("button", { role: "radio", "aria-checked": active, onClick: () => !active && onChange(o.key), class: `px-3 py-1.5 text-xs font-medium transition focus:outline-none focus:z-10
              ${active
                    ? 'bg-green-600 text-white dark:bg-green-500'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}
            `, children: o.label }, o.key));
        }) }));
};
